/**
 * @file Viewport-driven reveal (`reveal-component`) and phrase highlight (`highlighted-text` on `<em>`).
 */
import { Component } from "@theme/component";
import { animate } from "@theme/vendor";
import { observerElement, prefersReducedMotion, waitForMedia } from '@theme/utilities';

/**
 * `reveal-component`: viewport-triggered entrance (`data-name`), plus `rotate-words` (CSS on `.word-reveal`).
 * Carousel helpers: `refreshAnimation`, `resetAnimation`, `hideImmediately`, `reinitialize`.
 * Events: `reveal:finished`.
 */
export class RevealComponent extends Component {
  /** Entrance / reset duration for WAAPI (not `rotate-words`). */
  static #DURATION_MS = 500;
  /** Shared easing for entrance and reset animations. */
  static #EASING = 'cubic-bezier(0.25, 0.46, 0.45, 0.94)';
  /** IntersectionObserver threshold (% visible) passed to `observerElement`. */
  static #IO_THRESHOLD = 5;

  /** Active `animate()` handle; cancelled on replay or teardown. */
  #animation = null;
  /** Return value from `observerElement` (has `.disconnect`). */
  #observer = null;

  /** Delay before animation starts, from `data-delay` (defaults to `0`). */
  get delay() {
    const delay = parseInt(this.dataset.delay, 10);
    return isNaN(delay) ? 0 : delay;
  }

  /** Resolved animation id from `data-name`, or `fade`. */
  get animationName() {
    return this.dataset.name || 'fade';
  }

  /** Images, iframes, and SVGs inside the host; `startAnimation` waits for these via `waitForMedia`. */
  get media() {
    return Array.from(this.querySelectorAll("img, iframe, svg"));
  }

  /** When set (`data-skip-start`), skip applying hidden styles and skip running the entrance animation until the attribute is cleared. */
  get shouldSkipStart() {
    return this.hasAttribute("data-skip-start");
  }

  /** When set (`data-skip-init`), do not attach the intersection observer until `reinitialize()` runs. */
  get skipInitialize() {
    return this.hasAttribute("data-skip-init");
  }

  /**
   * Inline styles applied before the first frame so the element starts “off” (opacity/transform).
   * Not used for `rotate-words` (styling is word-level in CSS).
   * @returns {Object<string, string>}
   */
  #getInitialStyles() {
    const styles = {
      'fade': {
        opacity: '0',
      },
      'scale': {
        opacity: '0',
        transform: 'scale(0.5)'
      },
      'slide-left': {
        opacity: '0',
        transform: 'translateX(-20px)'
      },
      'slide-right': {
        opacity: '0',
        transform: 'translateX(20px)'
      },
      'slide-bottom': {
        opacity: '0',
        transform: 'translateY(20px)'
      }
    };
    return styles[this.animationName] || styles['fade'];
  }

  /**
   * Keyframes for the entrance: from `#getInitialStyles` values to fully visible.
   * @returns {Array<Record<string, number|string>>}
   */
  #getKeyframes() {
    const keyframes = {
      'fade': [
        { opacity: 0 },
        { opacity: 1 }
      ],
      'scale': [
        { opacity: 0, transform: 'scale(0.5)' },
        { opacity: 1, transform: 'scale(1)' }
      ],
      'slide-left': [
        { opacity: 0, transform: 'translateX(-20px)' },
        { opacity: 1, transform: 'translateX(0)' }
      ],
      'slide-right': [
        { opacity: 0, transform: 'translateX(20px)' },
        { opacity: 1, transform: 'translateX(0)' }
      ],
      'slide-bottom': [
        { opacity: 0, transform: 'translateY(20px)' },
        { opacity: 1, transform: 'translateY(0)' }
      ]
    };
    return keyframes[this.animationName] || keyframes['fade'];
  }

  /**
   * Keyframes for `resetAnimation`: visible → same hidden state as `#getInitialStyles`.
   * @returns {Array<Record<string, number|string>>}
   */
  #getResetKeyframes() {
    const keyframes = {
      'fade': [
        { opacity: 1 },
        { opacity: 0 }
      ],
      'scale': [
        { opacity: 1, transform: 'scale(1)' },
        { opacity: 0, transform: 'scale(0.5)' }
      ],
      'slide-left': [
        { opacity: 1, transform: 'translateX(0)' },
        { opacity: 0, transform: 'translateX(-20px)' }
      ],
      'slide-right': [
        { opacity: 1, transform: 'translateX(0)' },
        { opacity: 0, transform: 'translateX(20px)' }
      ],
      'slide-bottom': [
        { opacity: 1, transform: 'translateY(0)' },
        { opacity: 0, transform: 'translateY(20px)' }
      ]
    };
    return keyframes[this.animationName] || keyframes['fade'];
  }

  /**
   * Wires `rotate-words` DOM prep, optional hidden styles, and the scroll-into-view observer
   * (unless `data-skip-init`). No-op for users with reduced motion.
   */
  connectedCallback() {
    super.connectedCallback();
    if (prefersReducedMotion()) return;

    this.#prepareRotateWords();
    this.#preInitialize();

    if (!this.skipInitialize) {
      this.#attachViewportObserver();
    }
  }

  /** Disconnects any previous observer, then registers `observerElement` → `#initialize`. */
  #attachViewportObserver() {
    if (this.#observer && typeof this.#observer.disconnect === 'function') {
      this.#observer.disconnect();
    }
    this.#observer = observerElement({
      element: this,
      callback: this.#initialize.bind(this),
      threshold: RevealComponent.#IO_THRESHOLD
    });
  }

  /**
   * Replaces the viewport observer (e.g. after carousel setup). Call `removeAttribute('data-skip-init')`
   * first if the host was deferred with that flag.
   */
  reinitialize() {
    this.#attachViewportObserver();
  }

  /** Stops any in-flight WAAPI playback and disconnects the viewport observer. */
  disconnectedCallback() {
    this.#cancelAnimation();
    if (this.#observer && typeof this.#observer.disconnect === 'function') {
      this.#observer.disconnect();
    }
    this.#observer = null;
    super.disconnectedCallback();
  }

  /**
   * Applies hidden inline styles so the first paint is invisible until `startAnimation`.
   * Skips when `data-skip-start` is set, or when animation is `rotate-words`.
   */
  #preInitialize() {
    if (this.shouldSkipStart) return;
    if (this.animationName === 'rotate-words') return;
    const styles = this.#getInitialStyles();
    Object.assign(this.style, styles);
  }

  /** Intersection observer callback — runs `startAnimation()` when the host enters view. */
  #initialize() {
    this.startAnimation();
  }

  /**
   * Waits for nested media, then runs the entrance (`rotate-words` → `.animated` + event; else WAAPI).
   * No-op if `data-skip-start` is set.
   */
  async startAnimation() {
    if (this.shouldSkipStart) return;

    await waitForMedia(this.media);
    this.#cancelAnimation();

    if (this.animationName === 'rotate-words') {
      this.classList.add('animated');
      this.dispatchEvent(new CustomEvent('reveal:finished', { detail: { component: this } }));
      return;
    }

    const options = {
      duration: RevealComponent.#DURATION_MS,
      easing: RevealComponent.#EASING,
      delay: this.delay,
      fill: 'forwards'
    };

    this.style.setProperty('will-change', 'transform, opacity');
    this.#animation = await animate(this, this.#getKeyframes(), options);
    this.#animation.finished.then(() => {
      this.style.setProperty('will-change', 'auto');
      this.dispatchEvent(new CustomEvent('reveal:finished', { detail: { component: this } }));
    });
  }

  /**
   * Cancels WAAPI playback if present. For `rotate-words`, also removes `.animated` (CSS idle state).
   */
  #cancelAnimation() {
    if (this.#animation) {
      this.#animation.cancel();
      this.#animation = null;
    }

    if (this.animationName === 'rotate-words') {
      this.classList.remove('animated');
    }

  }

  /**
   * Replay entrance (e.g. carousel): clears `data-skip-start`, then either toggles `rotate-words` CSS
   * or re-applies hidden styles and runs `startAnimation` after `delayMs`.
   * @param {number} [delayMs=20] Stagger — carousel may pass `swiper.speed - 300`.
   */
  refreshAnimation(delayMs = 20) {
    if (prefersReducedMotion()) return;
    this.removeAttribute('data-skip-start');
    this.#cancelAnimation();

    const delay =
      typeof delayMs === 'number' && Number.isFinite(delayMs) ? delayMs : 20;

    if (this.animationName === 'rotate-words') {
      this.classList.remove('animated');
      setTimeout(() => {
        void this.offsetWidth;
        this.classList.add('animated');
        this.dispatchEvent(
          new CustomEvent('reveal:finished', { detail: { component: this } })
        );
      }, delay);
      return;
    }

    this.#preInitialize();
    setTimeout(() => {
      this.startAnimation();
    }, delay);
  }

  /**
   * Return to hidden / idle (e.g. leaving a slide). `rotate-words`: `#cancelAnimation` clears `.animated`.
   */
  async resetAnimation() {
    if (prefersReducedMotion()) return;

    this.#cancelAnimation();
    if (this.animationName === 'rotate-words') return;

    const options = {
      duration: RevealComponent.#DURATION_MS,
      delay: this.delay,
      easing: RevealComponent.#EASING,
      fill: 'forwards'
    };

    this.style.setProperty('will-change', 'transform, opacity');
    try {
      const animation = animate(this, this.#getResetKeyframes(), options);
      await animation.finished;
    } finally {
      this.style.setProperty('will-change', 'auto');
    }
  }

  /** Applies hidden styles synchronously; skips `rotate-words` (no host opacity animation). */
  hideImmediately() {
    this.#cancelAnimation();
    if (this.animationName === 'rotate-words') return;
    const styles = this.#getInitialStyles();
    Object.assign(this.style, styles);

    this.style.setProperty('will-change', 'auto');
  }

  /**
   * Word spans already produced by a previous run (used to avoid double-wrapping).
   * @returns {Element[]}
   */
  #getRotateWords() {
    return Array.from(this.querySelectorAll('.word-reveal__text'));
  }

  /**
   * For `rotate-words` only: splits each eligible text node on whitespace, wraps non-space tokens in
   * `.word-reveal` / `.word-reveal__text`, then strips empty `<strong>` left by RTE/markup.
   */
  #prepareRotateWords() {
    if (this.animationName !== 'rotate-words') return;
    if (this.#getRotateWords().length) return;

    this.#getRotatableTextNodes().forEach((textNode) => {
      const fragment = document.createDocumentFragment();

      textNode.textContent
        .split(/(\s+)/)
        .filter(Boolean)
        .forEach((token) => fragment.appendChild(this.#createRotateWordNode(token)));

      textNode.replaceWith(fragment);
    });

    this.#removeEmptyStrongElements();
  }

  /**
   * Removes `<strong>` with no visible text (after trim), including nested empties.
   * Deepest nodes are removed first so one pass is enough.
   */
  #removeEmptyStrongElements() {
    const componentRoot = this;
    const depthFromComponent = (strong) => {
      let depth = 0;
      for (
        let ancestor = strong;
        ancestor && ancestor !== componentRoot;
        ancestor = ancestor.parentElement
      ) {
        depth += 1;
      }
      return depth;
    };

    Array.from(this.querySelectorAll('strong'))
      .filter((strong) => !strong.textContent.trim())
      .sort(
        (firstStrong, secondStrong) =>
          depthFromComponent(secondStrong) - depthFromComponent(firstStrong)
      )
      .forEach((strong) => strong.remove());
  }

  /**
   * Text nodes to split for `rotate-words`: non-empty after trim, inside the host,
   * and not already under `.word-reveal`.
   * @returns {Text[]}
   */
  #getRotatableTextNodes() {
    const textNodes = [];
    const walker = document.createTreeWalker(this, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        if (!node.textContent?.trim()) return NodeFilter.FILTER_REJECT;
        if (!node.parentElement) return NodeFilter.FILTER_REJECT;
        if (node.parentElement.closest('.word-reveal')) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      }
    });

    while (walker.nextNode()) {
      textNodes.push(walker.currentNode);
    }

    return textNodes;
  }

  /**
   * One split token: whitespace stays a raw text node; words become a wrapper span + inner span.
   * @param {string} token
   * @returns {Node}
   */
  #createRotateWordNode(token) {
    if (/^\s+$/.test(token)) {
      return document.createTextNode(token);
    }

    const wordWrapper = document.createElement('span');
    wordWrapper.classList.add('word-reveal');

    const word = document.createElement('span');
    word.classList.add('word-reveal__text');
    word.textContent = token;

    wordWrapper.appendChild(word);
    return wordWrapper;
  }

}

if (!customElements.get('reveal-component')) {
  customElements.define('reveal-component', RevealComponent);
}

/**
 * `<em is="highlighted-text" class="highlighted-text">`: adds `.revealed` in view; CSS drives the look.
 * Same carousel API as `RevealComponent`: `reinitialize`, `refreshAnimation`, `resetAnimation`, `hideImmediately`.
 * Event: `highlight:revealed` (bubbles).
 */
class HighlightedText extends Component {
  /** See `RevealComponent.#observer`. */
  #observer = null;

  /** Without reduced motion: attaches observer unless `data-skip-init`. Else reveals once (no IO). */
  connectedCallback() {
    super.connectedCallback();
    if (prefersReducedMotion()) {
      this.#reveal();
      return;
    }
    if (!this.skipInitialize) {
      this.#attachViewportObserver();
    }
  }

  /** Disconnects the observer and clears the reference. */
  disconnectedCallback() {
    if (this.#observer && typeof this.#observer.disconnect === 'function') {
      this.#observer.disconnect();
    }
    this.#observer = null;
    super.disconnectedCallback();
  }

  /** Disconnects any previous observer, then creates a direct IO that fires only when element is actually visible. */
  #attachViewportObserver() {
    if (this.#observer && typeof this.#observer.disconnect === 'function') {
      this.#observer.disconnect();
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          observer.disconnect();
          this.#initialize();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(this);
    this.#observer = observer;
  }

  /** Intersection callback: applies `#reveal` when the phrase enters view. */
  #initialize() {
    this.#reveal();
  }

  /**
   * Replaces the viewport observer. Remove `data-skip-init` on the host first if it was set for clones.
   */
  reinitialize() {
    this.#attachViewportObserver();
  }

  /** One-shot reveal + event; skipped if already `.revealed` (duplicate IO callbacks). */
  #reveal() {
    if (this.classList.contains('revealed')) return;

    this.classList.add('revealed');
    this.dispatchEvent(
      new CustomEvent('highlight:revealed', {
        bubbles: true,
        detail: { element: this }
      })
    );
  }

  /** Removes `.revealed` (idle / hidden highlight state). */
  #cancelHighlight() {
    this.classList.remove('revealed');
  }

  /**
   * Clears `data-skip-init`, removes `.revealed`, then re-adds it after `delayMs` (replay CSS).
   * @param {number} [delayMs=20]
   */
  refreshAnimation(delayMs = 20) {
    if (prefersReducedMotion()) return;

    const delay =
      typeof delayMs === 'number' && Number.isFinite(delayMs) ? delayMs : 20;

    this.#cancelHighlight();
    setTimeout(() => {
      this.#reveal();
    }, delay);
  }

  /** Sync return to idle state (e.g. leaving a slide). */
  async resetAnimation() {
    if (prefersReducedMotion()) return;
    this.#cancelHighlight();
  }

  /** Same as `resetAnimation` for this component — instant hide, no transition API. */
  hideImmediately() {
    this.#cancelHighlight();
  }

  /** When set (`data-skip-init`), do not attach the observer until `reinitialize()` runs. */
  get skipInitialize() {
    return this.hasAttribute('data-skip-init');
  }
}

if (!customElements.get('highlighted-text')) {
  customElements.define('highlighted-text', HighlightedText);
}
